import * as React from 'react';

import { Outside } from '@cian/ui-kit/prev/outside';

import { ISuggestion, EGender } from '../../types/fio';
import { FIOSuggest } from '../../components/FIOSuggest';

import { getFillProgress, placeholders, useOnChangeHandler, useOnClickItemHandler } from './utils';

import { Placeholder } from '../../components/FIOSuggest/Components/Placeholder';
import { Suggest } from '../../components/FIOSuggest/Components/Suggest';
import { useOnFocusHandler } from './utils/useOnFocusHandler';
import { useInputValueSubscribe } from './utils/useInputValueSubscribe';
import { useGetFIOSuggest } from './utils/useGetFIOSuggest';

export interface IFIOContainerProps {
  onChange(inputValue: string, fillProgress: ReturnType<typeof getFillProgress>): void;
  initialValue?: string;
}

export function FIOContainer({ onChange, initialValue }: IFIOContainerProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = React.useState(initialValue || '');
  const [suggestPart, setSuggestPart] = React.useState('');
  const [placeholder, setPlaceholder] = React.useState(initialValue ? '' : placeholders.default);
  const [gender, setGender] = React.useState(EGender.UNKNOWN);
  const [suggestFiltered, setSuggestFiltered] = React.useState<ISuggestion[]>([]);

  const getFIOSuggest = useGetFIOSuggest();

  const resetSuggested = React.useCallback(() => {
    setSuggestPart('');
    setSuggestFiltered([]);
  }, []);

  const handleOnFocus = useOnFocusHandler({ gender, inputValue, setSuggestFiltered, setSuggestPart, getFIOSuggest });

  const handleOnChange = useOnChangeHandler({
    suggestFiltered,
    setInputValue,
    setSuggestFiltered,
    gender,
    setGender,
    setSuggestPart,
    setPlaceholder,
    resetSuggested,
    getFIOSuggest,
  });

  const handleOnClickItem = useOnClickItemHandler({
    inputValue,
    suggestFiltered,
    inputRef,
    resetSuggested,
    setInputValue,
    setPlaceholder,
    setGender,
  });

  useInputValueSubscribe({ onChange, inputValue });

  return (
    <Outside onOutside={resetSuggested}>
      <FIOSuggest
        inputRef={inputRef}
        onFocus={handleOnFocus}
        onChange={handleOnChange}
        inputValue={inputValue}
        placeholder={
          (suggestPart || placeholder) && (
            <Placeholder visiblePartText={suggestPart || placeholder} hidePartText={inputValue} />
          )
        }
        suggestFiltered={
          Boolean(suggestFiltered.length) && <Suggest suggest={suggestFiltered} handleClickItem={handleOnClickItem} />
        }
      />
    </Outside>
  );
}
